import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getToken } from '../../whatsapp/function/token';
import domain from '../../reusecomponent/domain';
import {
  FaArrowLeft, FaPaperclip, FaTrash, FaSpinner,
  FaPaperPlane, FaLock, FaFolderOpen, FaTimesCircle,
  FaFilePdf, FaImage, FaDownload, FaEye
} from 'react-icons/fa';

const AuthenticatedImage = ({ fileName, alt, style, className, onClick }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchImage = async () => {
      try {
        const userToken = getToken();
        const response = await axios.get(`${domain()}/tickets/attachments/${fileName}`, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
          },
          responseType: 'blob'
        });
        if (active) {
          const objectUrl = URL.createObjectURL(response.data);
          setSrc(objectUrl);
        }
      } catch (err) {
        console.error('Error loading secure image:', err);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchImage();

    return () => {
      active = false;
      if (src) {
        URL.revokeObjectURL(src);
      }
    };
  }, [fileName]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-light" style={{ ...style, height: '150px' }}>
        <FaSpinner className="spinner-border border-0 text-success p-0 m-0" style={{ width: '1.5rem', height: '1.5rem', color: '#075E54' }} />
      </div>
    );
  }

  return (
    <img
      src={src || ''}
      alt={alt}
      style={style}
      className={className}
      onClick={onClick}
    />
  );
};

const PreviewModal = ({ fileName, type, onClose }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchFile = async () => {
      try {
        const userToken = getToken();
        const response = await axios.get(`${domain()}/tickets/attachments/${fileName}`, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
          },
          responseType: 'blob'
        });
        if (active) {
          const objectUrl = URL.createObjectURL(response.data);
          setSrc(objectUrl);
        }
      } catch (err) {
        console.error('Error loading preview file:', err);
        if (active) setError('Failed to load file preview.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchFile();

    return () => {
      active = false;
      if (src) {
        URL.revokeObjectURL(src);
      }
    };
  }, [fileName]);

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 1060 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        style={{ maxWidth: '800px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0 shadow" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <div className="modal-header border-bottom bg-light px-4 py-3 d-flex justify-content-between align-items-center">
            <h6 className="modal-title fw-bold text-dark mb-0 text-truncate" style={{ maxWidth: '80%' }}>
              {fileName}
            </h6>
            <button
              type="button"
              className="btn-close shadow-none border-0"
              onClick={onClose}
              style={{ cursor: 'pointer' }}
            ></button>
          </div>
          <div className="modal-body p-0 bg-dark d-flex align-items-center justify-content-center" style={{ minHeight: '400px', maxHeight: '75vh', overflow: 'auto' }}>
            {loading ? (
              <div className="d-flex flex-column align-items-center justify-content-center text-white py-5">
                <FaSpinner className="spinner-border border-0 text-white p-0 mb-3" style={{ width: '2.5rem', height: '2.5rem' }} />
                <span>Loading preview...</span>
              </div>
            ) : error ? (
              <div className="text-white p-4 text-center">{error}</div>
            ) : type === 'image' ? (
              <img
                src={src}
                alt={fileName}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            ) : (
              <iframe
                src={src}
                title={fileName}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              ></iframe>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const downloadSecureFile = async (fileName) => {
  try {
    const userToken = getToken();
    const response = await axios.get(`${domain()}/tickets/attachments/${fileName}`, {
      headers: {
        'Authorization': `Bearer ${userToken}`,
      },
      responseType: 'blob'
    });

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Error downloading file:', err);
    Swal.fire({
      title: 'Error',
      text: 'Failed to download file.',
      icon: 'error',
      confirmButtonColor: '#075E54'
    });
  }
};

export default function TicketDetail() {
  const { id: encodedId } = useParams();
  const id = encodedId; // Use encoded ID directly for backend API routes
  const navigate = useNavigate();

  const getBaseUrl = () => {
    const d = domain();
    return d.replace(/\/web$/, '').replace(/\/api$/, '');
  };

  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Attachment Preview State
  const [previewFile, setPreviewFile] = useState(null);

  // Reply Form States
  const [replyMessage, setReplyMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [replying, setReplying] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);

  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  // Headers helper
  const getHeaders = (isMultipart = false) => {
    const userToken = getToken();
    const headers = {
      'Authorization': `Bearer ${userToken}`,
      'Accept': 'application/json'
    };
    if (isMultipart) {
      headers['Content-Type'] = 'multipart/form-data';
    }
    return headers;
  };

  const fetchTicketDetail = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${domain()}/tickets/${id}`, {
        headers: getHeaders()
      });
      if (response.data && response.data.status) {
        setTicket(response.data.data);
        setReplies(response.data.data.replies || []);
      } else {
        setError(response.data.message || 'Failed to load ticket details.');
      }
    } catch (err) {
      console.error('Error fetching ticket detail:', err);
      setError(err.response?.data?.message || 'Something went wrong while fetching ticket detail.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketDetail();
  }, [id]);

  // Scroll to bottom of chat when replies update
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies]);

  // Reopen ticket action
  const handleReopen = async () => {
    setReopening(true);
    try {
      const response = await axios.post(`${domain()}/tickets/${id}/reopen`, {}, {
        headers: getHeaders()
      });

      Swal.fire({
        title: 'Ticket Reopened',
        text: response.data.message || 'Ticket reopened successfully!',
        icon: 'success',
        confirmButtonColor: '#075E54'
      });
      fetchTicketDetail(false);
    } catch (error) {
      console.error('Error reopening ticket:', error);
      if (error.response && error.response.status === 400) {
        // Displays: "This ticket has been closed by an admin and cannot be reopened. Please raise a new ticket if you have further queries."
        Swal.fire({
          title: 'Cannot Reopen',
          text: error.response.data.message,
          icon: 'warning',
          confirmButtonColor: '#dc3545'
        });
      } else {
        Swal.fire('Error', error.response?.data?.message || 'Failed to reopen ticket.', 'error');
      }
    } finally {
      setReopening(false);
    }
  };

  // Close ticket action
  const handleClose = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to close this ticket?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#075E54',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, close it!'
    });

    if (!result.isConfirmed) return;

    setClosing(true);
    try {
      const response = await axios.post(`${domain()}/tickets/${id}/close`, {}, {
        headers: getHeaders()
      });

      Swal.fire({
        title: 'Ticket Closed',
        text: response.data.message || 'Ticket closed successfully.',
        icon: 'success',
        confirmButtonColor: '#075E54'
      });
      fetchTicketDetail(false);
    } catch (error) {
      console.error('Error closing ticket:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to close ticket.', 'error');
    } finally {
      setClosing(false);
    }
  };

  // Reply form file selector validation
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];

    for (let file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('File Too Large', `File "${file.name}" exceeds the 10MB limit.`, 'warning');
        continue;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire('Unsupported Format', `File "${file.name}" must be a PDF or Image.`, 'warning');
        continue;
      }

      validFiles.push(file);
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Send reply
  const handleSendReply = async (e) => {
    e.preventDefault();

    if (!replyMessage.trim() && files.length === 0) {
      return;
    }

    setReplying(true);

    const formData = new FormData();
    formData.append('message', replyMessage);
    files.forEach((file) => {
      formData.append('files[]', file);
    });

    try {
      const response = await axios.post(`${domain()}/tickets/${id}/reply`, formData, {
        headers: getHeaders(true)
      });

      setReplyMessage('');
      setFiles([]);
      fetchTicketDetail(false);
    } catch (error) {
      console.error('Error sending reply:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to send reply.', 'error');
    } finally {
      setReplying(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#e0f7fa', color: '#00838f', fontSize: '12px' }}>
            Open
          </span>
        );
      case 'closed':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#f1f3f5', color: '#495057', fontSize: '12px' }}>
            Closed
          </span>
        );
      case 'closed_by_admin':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#fde8e8', color: '#c81e1e', fontSize: '12px' }}>
            Closed
          </span>
        );
      case 'closed_by_user':
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#f1f3f5', color: '#495057', fontSize: '12px' }}>
            Closed
          </span>
        );
      default:
        return (
          <span className="badge px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#f1f3f5', color: '#495057', fontSize: '12px' }}>
            {status}
          </span>
        );
    }
  };

  const isClosed = ticket?.status === 'closed' || ticket?.status === 'closed_by_admin' || ticket?.status === 'closed_by_user';

  return (
    <div className="col-lg-12 grid-margin stretch-card p-0 px-sm-3">
      {/* Main card wrapper */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', backgroundColor: '#ffffff' }}>
        <div className="card-body">

          {/* Back link */}
          <button
            className="btn btn-link text-decoration-none d-inline-flex align-items-center gap-2 text-secondary mb-3 p-0"
            onClick={() => navigate('/client-area/whatsapp/tickets')}
            style={{ fontSize: '14px' }}
          >
            <FaArrowLeft /> Back to Tickets
          </button>

          {loading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <FaSpinner className="spinner-border border-0 text-success p-0 mb-3" style={{ width: '3rem', height: '3rem', color: '#075E54' }} />
              <span className="text-secondary fw-semibold">Loading conversation history...</span>
            </div>
          ) : error ? (
            <div className="alert alert-danger border-0 rounded-3 p-4 text-center" role="alert" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <h4 className="alert-heading fw-bold mb-2">Error</h4>
              <p className="mb-0 text-muted">{error}</p>
              <button className="btn btn-outline-danger mt-3 px-4 rounded-pill" onClick={() => fetchTicketDetail(true)}>
                Retry
              </button>
            </div>
          ) : !ticket ? (
            <div className="text-center py-5">Ticket not found.</div>
          ) : (
            <div className="d-flex flex-column" style={{ height: 'calc(100vh - 220px)', minHeight: '520px' }}>

              {/* Header Panel */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 pb-3 mb-3 border-bottom">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <span className="text-secondary small fw-bold">Ticket #{ticket.ticket_no}</span>
                    {getStatusBadge(ticket.status)}
                  </div>
                  <h5 className="fw-bold mb-0 text-dark" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.25rem' }}>{ticket.subject}</h5>
                </div>

                <div className="d-flex align-items-center gap-2">
                  {isClosed ? (
                    <button
                      className="btn text-white px-4 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
                      style={{ backgroundColor: '#075E54', borderColor: '#075E54', borderRadius: '8px', fontSize: '13px' }}
                      onClick={handleReopen}
                      disabled={reopening}
                    >
                      {reopening ? <FaSpinner className="spinner-border border-0 text-white p-0 m-0" style={{ width: '1rem', height: '1rem' }} /> : null}
                      Reopen Ticket
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline-danger px-4 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      onClick={handleClose}
                      disabled={closing}
                    >
                      {closing ? <FaSpinner className="spinner-border border-0 text-danger p-0 m-0" style={{ width: '1rem', height: '1rem' }} /> : null}
                      Close Ticket
                    </button>
                  )}
                </div>
              </div>

              {/* Conversation Area */}
              <div
                className="flex-grow-1 overflow-auto bg-light p-4 mb-3 border"
                style={{
                  backgroundImage: "url('https://api.campaigningtool.com/tickets/attachments/wa_chat_bg.png')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: '#efeae2',
                  borderRadius: '12px'
                }}
              >
                {replies.length === 0 ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                    <FaFolderOpen size={48} className="mb-2 opacity-50" />
                    <span className="fw-medium">No replies in this thread yet.</span>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {replies.map((reply, index) => {
                      const isUser = reply.sender_type === 'user';
                      return (
                        <div
                          key={reply.id || index}
                          className={`d-flex flex-column ${isUser ? 'align-items-end' : 'align-items-start'}`}
                        >
                          {/* Bubble */}
                          <div
                            className="p-3 shadow-sm position-relative max-width-600"
                            style={{
                              backgroundColor: isUser ? '#d9fdd3' : '#ffffff',
                              color: '#303030',
                              borderRadius: isUser ? '16px 16px 0px 16px' : '16px 16px 16px 0px',
                            }}
                          >
                            {/* Sender Label */}
                            <div className="fw-bold small mb-1" style={{ color: isUser ? '#075E54' : '#128C7E' }}>
                              {isUser ? 'You' : 'Support Agent'}
                            </div>

                            {/* Message content */}
                            <p className="mb-2 text-wrap whitespace-pre-wrap text-dark" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                              {reply.message}
                            </p>

                            {/* Attachments rendering */}
                            {reply.attachments && reply.attachments.length > 0 && (
                              <div className="d-flex flex-column gap-2 mt-2 pt-2 border-top border-black border-opacity-10">
                                {reply.attachments.map((file) => {
                                  const isImage = ['jpg', 'jpeg', 'png', 'gif'].some(ext => file.file_name?.toLowerCase().endsWith(ext));

                                  return (
                                    <div key={file.id} className="border rounded-3 overflow-hidden bg-white max-width-300 p-2 shadow-sm">
                                      <div className="d-flex align-items-center gap-2 mb-2 text-truncate">
                                        {isImage ? (
                                          <FaImage size={22} className="text-success flex-shrink-0" />
                                        ) : (
                                          <FaFilePdf size={22} className="text-danger flex-shrink-0" />
                                        )}
                                        <span className="text-dark small text-truncate fw-medium" style={{ fontSize: '12px' }}>{file.file_name}</span>
                                      </div>
                                      <div className="d-flex align-items-center justify-content-end gap-1 border-top pt-2">
                                        <button
                                          type="button"
                                          className="btn btn-light btn-sm px-2 py-1 d-flex align-items-center gap-1"
                                          onClick={() => setPreviewFile({ name: file.file_name, type: isImage ? 'image' : 'pdf' })}
                                          style={{ fontSize: '11px', borderRadius: '4px' }}
                                        >
                                          <FaEye size={11} /> View
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-light btn-sm px-2 py-1 d-flex align-items-center gap-1 text-decoration-none"
                                          onClick={() => downloadSecureFile(file.file_name)}
                                          style={{ fontSize: '11px', borderRadius: '4px' }}
                                        >
                                          <FaDownload size={11} /> Download
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Time stamp */}
                            <div className="text-end text-muted small" style={{ fontSize: '10px', marginTop: '4px' }}>
                              {new Date(reply.created_at).toLocaleTimeString(undefined, {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </div>

              {/* Action / Reply Bar */}
              {isClosed ? (
                <div className="alert border p-3 text-center mb-0 d-flex flex-column align-items-center justify-content-center bg-white shadow-sm"
                  style={{
                    borderRadius: '12px',
                    borderColor: '#cbd5e1',
                    color: ticket.status === 'closed_by_admin' ? '#dc3545' : '#6c757d'
                  }}>
                  <FaLock size={20} className="mb-2" />
                  <h6 className="fw-bold mb-1" style={{ color: '#0f172a' }}>
                    {ticket.status === 'closed_by_admin' ? 'Ticket Closed' : 'Conversation Closed'}
                  </h6>
                  <p className="mb-0 small max-width-600 text-muted">
                    {ticket.status === 'closed_by_admin'
                      ? "This ticket has been closed by an admin and cannot be reopened. Please raise a new ticket if you have further queries."
                      : "This conversation has been marked as resolved. If you still need help, click the 'Reopen Ticket' button above to continue."}
                  </p>
                </div>
              ) : (
                <div className="card border-0 shadow-sm bg-white overflow-visible" style={{ borderRadius: '12px', border: '1px solid #cbd5e1' }}>
                  <div className="card-body p-3">
                    <form onSubmit={handleSendReply}>

                      {/* Selected files indicator */}
                      {files.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-2 p-2 border-bottom">
                          {files.map((file, idx) => (
                            <div key={idx} className="badge bg-light text-dark border p-2 rounded-3 d-flex align-items-center gap-2" style={{ borderRadius: '6px' }}>
                              <FaPaperclip size={10} className="text-secondary" />
                              <span className="text-truncate font-normal" style={{ maxWidth: '120px' }}>{file.name}</span>
                              <button
                                type="button"
                                className="btn btn-link text-danger p-0 m-0 border-0 shadow-none d-flex align-items-center"
                                onClick={() => removeFile(idx)}
                              >
                                <FaTimesCircle size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Input row */}
                      <div className="d-flex align-items-center gap-2">

                        {/* Attachment button */}
                        <button
                          type="button"
                          className="btn btn-light rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: '40px', height: '40px' }}
                          onClick={() => fileInputRef.current.click()}
                          disabled={replying}
                          title="Attach file (Max 10MB JPG/PNG/PDF)"
                        >
                          <FaPaperclip className="text-secondary" />
                        </button>
                        <input
                          type="file"
                          multiple
                          className="d-none"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/jpeg,image/png,image/gif,application/pdf"
                          disabled={replying}
                        />

                        {/* Message input */}
                        <input
                          type="text"
                          className="form-control rounded-pill py-2 px-3 border bg-light text-dark flex-grow-1"
                          placeholder="Type a message..."
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          disabled={replying}
                          style={{ outline: 'none', boxShadow: 'none', borderRadius: '20px', padding: '10px 15px', fontSize: '14px' }}
                        />

                        {/* Send Button */}
                        <button
                          type="submit"
                          className="btn text-white rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm"
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#075E54',
                            border: 'none',
                            opacity: !replyMessage.trim() && files.length === 0 ? 0.6 : 1
                          }}
                          disabled={(!replyMessage.trim() && files.length === 0) || replying}
                        >
                          {replying ? (
                            <FaSpinner className="spinner-border border-0 text-white p-0 m-0" style={{ width: '1rem', height: '1rem' }} />
                          ) : (
                            <FaPaperPlane size={14} style={{ marginLeft: '2px' }} />
                          )}
                        </button>

                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Attachment Preview Modal */}
              {previewFile && (
                <PreviewModal
                  fileName={previewFile.name}
                  type={previewFile.type}
                  onClose={() => setPreviewFile(null)}
                />
              )}

              <style>{`
                .max-width-600 {
                  max-width: 600px;
                }
                .max-width-300 {
                  max-width: 300px;
                }
                @media (max-width: 768px) {
                  .max-width-600 {
                    max-width: 85%;
                  }
                }
                .group-hover:hover .hover-show {
                  display: flex !important;
                }
                .attachment-card {
                  transition: transform 0.15s ease-in-out;
                }
                .attachment-card:hover {
                  transform: translateY(-2px);
                }
                .btn-xs {
                  padding: 1px 5px;
                  font-size: .75rem;
                  line-height: 1.5;
                  border-radius: 3px;
                }
              `}</style>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}
